/**
 * Philosophy page — presents the core beliefs and vision behind Zeytune.
 * Accessible via the "Philosophy" link in the landing page nav bar.
 */
import React from 'react';

type PhilosophyPageProps = {
    onBack: () => void;
};

export default function PhilosophyPage({ onBack }: PhilosophyPageProps) {
    return (
        <div className='info-page'>
            <div className='info-page-inner'>
                <button onClick={onBack} className='policy-back-btn'>
                    ← Back
                </button>

                <h1 className='info-page-display-title'>Philosophy</h1>

                <div className='info-page-body'>
                    <p className='info-page-para info-page-drop-cap'>
                        {/* Placeholder — content to be provided by the author */}
                        I have always strongly held the belief that user experience is what should drive the need for new technology in your application, not the other way around. We are all well aware of how extremely powerful emerging tech is completely reshaping the way we work, and there is immense temptation to augment our applications by adding a layer of the new technology as one would apply varnish over wood. It's more than obvious how technology driven products are beginning to flood the markets, and existing enterprises are quickly adapting by adding that layer of tech to stay relevant.
                    </p>
                    <p className='info-page-para'>
                        Zeytune serves as a small and humble reminder that prioritizing and conceiving a simple, fluid experience is sometimes just what the user needs. This app is not just another diagramming or brainstorming tool, but was built out of my personal need to easily and intuitively ideate in an engaging and accessible environment. I built the minimum viable version of this product by using it, and because of that sometimes it all felt very surreal.
                    </p>
                    <p className='info-page-para'>
                        My fascination and life-long association with engineering design and geometry is probably also that heavily influences this interactivity-first approach to ideation. But I am pretty sure many product folk prefer laying things out infront of them, looking at the big picture, and thinking deeply, as opposed to hammering away on spreadsheets and PRD's. You will probably also notice tiny little quirks that would give away the fact that this app is more or less OCD compliant!
                    </p>
                    <p className='info-page-para'>
                        And there is still a long way to go. At the time of writing of this introductory message, I have only focused on enabling an easy to use Opportunity-Solution-Trees workflow, which is something that I prefer working with. I am, however, a strong believer in context based evolution, and will continue to augment the product with additional functionality that product people like to use every day, without losing track of the core principles of Zeytune.
                    </p>
                    <p className='info-page-para'>
                        The word Zeytune is Persian, Arabic, Urdu, Trukish, Hebrew, Kurdish for olive (and definitely many other languages as well), a tree well known and regarded in the Middle East as a symbol of universal and pure wisdom. I hope this app helps you in your journey to create beautiful experiences and to think more deeply about how you can make a lasting impact on this world.
                    </p>
                    <p className='info-page-para'>
                        <b>Farhan.</b>
                    </p>

                    {/* LinkedIn link shown below the signature */}
                    <div className='philosophy-author-footer'>
                        <a
                            href='https://www.linkedin.com/in/farhan3d'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='philosophy-linkedin-link'
                            aria-label='LinkedIn profile'
                        >
                            {/* Official LinkedIn "in" logo — inline SVG, no external dependency */}
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                viewBox='0 0 24 24'
                                className='philosophy-linkedin-icon'
                                aria-hidden='true'
                            >
                                <path d='M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' />
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
